import AppContext from 'App/AppContext';
import { IPDropdown, IPLabel, IPSelectableOption, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import HrefLink from 'components/Links/HrefLink';
import TextInfo from 'components/Notification/Info';
import AuthSchemaPicker from 'components/Pickers/AuthSchemaPicker';
import KeyValueTagPicker from 'components/Pickers/KeyValueTagPicker';
import LocalizedDatePicker from 'components/Pickers/LocalizedDatePicker';
import { OwnerPicker } from 'components/Pickers/OwnerPicker';
import {
  getProcessingActivityRoleOptions,
  getProcessingActivityStateOptions,
} from 'components/ProcessingActivity/ProcessingActivityOptions';
import StringValueSelect from 'components/StringValue/StringValueSelect';
import { KeyValueTags } from 'components/Tags/KeyValueTag';
import { PropertySet, PropertySetLabelValue } from 'components/Utils/PropertySet';
import {
  deleteIcon,
  globalStackTokensGapSmall,
  globalStackTokensPropertySet,
  suppliersAndAssetsIcon,
} from 'globalStyles';
import { TFunction } from 'i18next';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import { Role } from 'models/auth/role';
import { PermissionTypes } from 'models/auth/rolePermission';
import { Owner } from 'models/owner';
import ProcessingActivity, { ProcessingActivityRole, ProcessingActivityState } from 'models/processingActivity';
import ReportDefinition, { ReportType } from 'models/reports/ReportDefinition';
import StringValue, { StringValueTypes } from 'models/stringValue/stringValue';
import Tag from 'models/tag';
import User from 'models/user';
import { FormEvent, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FeatureTypes,
  hasUserDataPermission,
  hasUserFeature,
  hasUserFeatureGenericManager,
  hasUserRolePermission,
} from 'services/Auth/featurePermissions';
import { getDateTimeDiffDays, toLocaleDateShort } from 'utils/datetime';

interface ITabDetailsEditableHeader {
  processingActivity: ProcessingActivity | undefined;
  updateProcessingActivity: (processingActivity: ProcessingActivity) => void;
  onCodeErrorMessage: () => Promise<string>;
  readOnly: boolean;
  unFocusKey: string;
  setSelectedReport: (report: ReportDefinition) => void;
}

export const TabDetailsEditableHeader = (props: ITabDetailsEditableHeader) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['processingActivity', 'adminAuth', 'reports']);

  const updateOwner = (item: User | Role | undefined) => {
    const newItem = props.processingActivity?.clone();
    if (newItem) {
      Owner.applyItem(newItem, item);
      props.updateProcessingActivity(newItem);
    }
  };

  const updateRole = (role: ProcessingActivityRole) => {
    const newProcessingActivity = props.processingActivity?.clone();
    if (newProcessingActivity) {
      newProcessingActivity.role = role;
      props.updateProcessingActivity(newProcessingActivity);
    }
  };

  const updateState = (state: ProcessingActivityState) => {
    const newProcessingActivity = props.processingActivity?.clone();
    if (newProcessingActivity) {
      if (state === ProcessingActivityState.Concept || state === ProcessingActivityState.AwaitingApproval) {
        newProcessingActivity.startDate = undefined;
        newProcessingActivity.endDate = undefined;
        newProcessingActivity.approvalDate = undefined;
      }
      if (state === ProcessingActivityState.Approved) {
        if (!newProcessingActivity.startDate) newProcessingActivity.startDate = new Date();
        newProcessingActivity.endDate = undefined;
      }
      if (state === ProcessingActivityState.Archived && !newProcessingActivity.endDate) {
        newProcessingActivity.endDate = new Date();
        if (
          newProcessingActivity.startDate &&
          getDateTimeDiffDays(newProcessingActivity.endDate, newProcessingActivity.startDate) > 0
        ) {
          newProcessingActivity.endDate = newProcessingActivity.startDate;
        }
      }
      newProcessingActivity.state = state;
      props.updateProcessingActivity(newProcessingActivity);
    }
  };

  const updateStateForLifeCycleStart = (
    processingActivity: ProcessingActivity,
    start: Date | undefined,
  ): ProcessingActivityState => {
    let newState: ProcessingActivityState = processingActivity.state;
    if (start && !processingActivity.endDate) {
      newState = ProcessingActivityState.Approved;
    } else if (processingActivity.endDate) {
      newState = ProcessingActivityState.Archived;
    } else {
      newState = ProcessingActivityState.Concept;
    }

    return newState;
  };

  const updateStateForLifeCycleEnd = (
    processingActivity: ProcessingActivity,
    end: Date | undefined,
  ): ProcessingActivityState => {
    let newState: ProcessingActivityState = processingActivity.state;
    if (end) {
      newState = ProcessingActivityState.Archived;
    } else if (processingActivity.startDate) {
      newState = ProcessingActivityState.Approved;
    } else {
      newState = ProcessingActivityState.Concept;
    }

    return newState;
  };

  const onAddTag = (tag: Tag) => {
    const newprocessingActivity = props.processingActivity?.clone();
    if (newprocessingActivity) {
      newprocessingActivity.tagIds?.push(tag.tagId);
      props.updateProcessingActivity(newprocessingActivity);
    }
  };

  const onRemoveTag = (tag: Tag) => {
    const newprocessingActivity = props.processingActivity?.clone();
    if (newprocessingActivity) {
      newprocessingActivity.tagIds = newprocessingActivity.tagIds?.filter((id) => id !== tag.tagId);
      props.updateProcessingActivity(newprocessingActivity);
    }
  };

  const onCreateTag = (tag: Tag) => {
    const newprocessingActivity = props.processingActivity?.clone();
    if (newprocessingActivity) {
      newprocessingActivity.newTags.push(tag);
      props.updateProcessingActivity(newprocessingActivity);
    }
  };

  const openReport = () => {
    const report = new ReportDefinition(ReportType.ProcessingActivityDetails, t('reports:Report20.Name'));
    report.description = t('reports:Report20.Description');
    report.icon = 'report.png';
    report.disableExcelExport = true;
    props.setSelectedReport(report);
  };

  const getRetentionPeriodText = (): string => {
    if (!props.processingActivity || props.processingActivity.retentionPeriod) return '';

    const retentionPeriodAssets = props.processingActivity.data.filter((asset) => asset.retentionPeriod);
    if (retentionPeriodAssets.length === 0) return '';

    const groupedRetentionPeriods = retentionPeriodAssets.reduce<Record<string, string[]>>((groups, asset) => {
      const retentionPeriod = asset.retentionPeriod?.value;
      if (!retentionPeriod) return groups;

      groups[retentionPeriod] = [...(groups[retentionPeriod] ?? []), asset.name];

      return groups;
    }, {});

    const retentionPeriods = Object.keys(groupedRetentionPeriods);
    if (retentionPeriods.length === 1) return retentionPeriods[0];

    return retentionPeriods
      .map((retentionPeriod) => `${retentionPeriod} (${groupedRetentionPeriods[retentionPeriod].join(', ')})`)
      .join(', ');
  };

  const getFocusChildren = (): JSX.Element | null => {
    if (!props.processingActivity) return null;

    return (
      <IPStack tokens={globalStackTokensGapSmall}>
        <IPStack horizontal wrap horizontalAlign="space-between" tokens={globalStackTokensGapSmall}>
          <IPStack.Item grow={1}>
            <IPTextField
              label={t('processingActivity:TabDetails.Fields.Code.Label')}
              value={props.processingActivity?.code ?? ''}
              onGetErrorMessage={props.onCodeErrorMessage}
              onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
                const newProcessingActivity = props.processingActivity?.clone();
                if (newProcessingActivity) {
                  newProcessingActivity.code = newValue ? newValue : '';
                  props.updateProcessingActivity(newProcessingActivity);
                }
              }}
              placeholder={t('processingActivity:TabDetails.Fields.Code.Placeholder')}
              readOnly={props.readOnly}
            />
          </IPStack.Item>
          <IPStack.Item grow={2}>
            <OwnerPicker
              selectedItem={props.processingActivity}
              onSelect={updateOwner}
              disabled={
                props.readOnly ||
                !hasUserDataPermission(
                  appContext,
                  [props.processingActivity?.authSchemaId],
                  AuthSchemaLineOperation.Update,
                )
              }
            />
          </IPStack.Item>
          {hasUserFeature(appContext, FeatureTypes.RBAC) && (
            <IPStack.Item grow={2}>
              <AuthSchemaPicker
                selectedAuthSchemaId={props.processingActivity?.authSchemaId}
                onSelect={(schema) => {
                  if (!props.processingActivity) return;
                  const newProcessingActivity = props.processingActivity.clone();
                  newProcessingActivity.authSchemaId = schema?.authSchemaId;
                  props.updateProcessingActivity(newProcessingActivity);
                }}
              />
            </IPStack.Item>
          )}
          <IPStack.Item grow={2}>
            <IPDropdown
              label={t('processingActivity:TabDetails.Fields.Role')}
              options={getProcessingActivityRoleOptions()}
              selectedKey={props.processingActivity?.role}
              onChange={(event: FormEvent<HTMLDivElement>, option?: IPSelectableOption, index?: number) => {
                if (!option) return;
                updateRole(option.data);
              }}
              disabled={props.readOnly}
              styles={{ root: { minWidth: 150 } }}
            />
          </IPStack.Item>
          <IPStack.Item grow={2}>
            <IPDropdown
              label={t('processingActivity:TabDetails.Fields.State')}
              info={
                !hasUserRolePermission(appContext, PermissionTypes.ApproveProcessingActivity)
                  ? t('processingActivity:TabDetails.Fields.StateInfo')
                  : undefined
              }
              options={getProcessingActivityStateOptions()}
              selectedKey={props.processingActivity?.state}
              onChange={(event: FormEvent<HTMLDivElement>, option?: IPSelectableOption, index?: number) => {
                if (!option) return;
                updateState(option.data);
              }}
              disabled={
                props.readOnly ||
                !hasUserRolePermission(appContext, PermissionTypes.ApproveProcessingActivity) ||
                props.processingActivity?.state === ProcessingActivityState.AwaitingApproval
              }
              styles={{ root: { minWidth: 150 } }}
            />
          </IPStack.Item>
          <IPStack.Item>
            <LocalizedDatePicker
              label={t('processingActivity:TabDetails.Fields.StartDate')}
              value={props.processingActivity.startDate}
              maxDate={props.processingActivity.endDate}
              showWeekNumbers={true}
              onDateChange={(value) => {
                if (!props.processingActivity) return;
                const newProcessingActivity = props.processingActivity.clone();
                if (value) {
                  const newDate = value;
                  if (props.processingActivity.startDate) {
                    newDate.setHours(props.processingActivity.startDate.getHours());
                    newDate.setMinutes(props.processingActivity.startDate.getMinutes());
                  }
                  newProcessingActivity.startDate = newDate;
                } else {
                  newProcessingActivity.startDate = undefined;
                }
                if (hasUserRolePermission(appContext, PermissionTypes.ApproveProcessingActivity)) {
                  newProcessingActivity.state = updateStateForLifeCycleStart(newProcessingActivity, value);
                }
                props.updateProcessingActivity(newProcessingActivity);
              }}
              allowEmptyValue={true}
              styles={{ root: { maxWidth: 300, minWidth: 200 } }}
            />
          </IPStack.Item>
          <IPStack.Item>
            <LocalizedDatePicker
              label={t('processingActivity:TabDetails.Fields.EndDate')}
              value={props.processingActivity.endDate}
              minDate={props.processingActivity.startDate}
              showWeekNumbers={true}
              onDateChange={(value) => {
                if (!props.processingActivity) return;
                const newProcessingActivity = props.processingActivity.clone();
                if (value) {
                  const newDate = value;
                  if (props.processingActivity.endDate) {
                    newDate.setHours(props.processingActivity.endDate.getHours());
                    newDate.setMinutes(props.processingActivity.endDate.getMinutes());
                  }
                  newProcessingActivity.endDate = newDate;
                } else {
                  newProcessingActivity.endDate = undefined;
                }
                if (hasUserRolePermission(appContext, PermissionTypes.ApproveProcessingActivity)) {
                  newProcessingActivity.state = updateStateForLifeCycleEnd(newProcessingActivity, value);
                }
                props.updateProcessingActivity(newProcessingActivity);
              }}
              allowEmptyValue={true}
              styles={{ root: { maxWidth: 300, minWidth: 200 } }}
            />
          </IPStack.Item>
          <IPStack.Item>
            <StringValueSelect
              label={t('processingActivity:TabDetails.Fields.RetentionPeriod')}
              info={t('processingActivity:TabDetails.Fields.RetentionPeriodInfo')}
              stringValueType={StringValueTypes.RetentionPeriod}
              canAddNew={true}
              onChange={(value: StringValue | undefined) => {
                const newProcessingActivity = props.processingActivity?.clone();
                if (newProcessingActivity) {
                  newProcessingActivity.retentionPeriod = value;
                  props.updateProcessingActivity(newProcessingActivity);
                }
              }}
              emptyDisplayText={
                props.processingActivity.data.length > 0
                  ? t('processingActivity:TabDetails.Fields.RetentionPeriodFromAssets')
                  : undefined
              }
              setEmptyText={
                props.processingActivity.data.length > 0
                  ? t('processingActivity:TabDetails.Fields.RetentionPeriodFromAssets')
                  : t('processingActivity:TabDetails.Fields.ClearRetentionPeriod')
              }
              setEmptyIcon={props.processingActivity.data.length > 0 ? suppliersAndAssetsIcon : deleteIcon}
              currentValueId={props.processingActivity.retentionPeriod?.stringValueId}
              isReadOnly={props.readOnly}
            />
          </IPStack.Item>
          <IPStack.Item grow={2}>
            <TextInfo message={t('processingActivity:TabDetails.Fields.Tags.Info')}>
              <IPLabel>{t('processingActivity:TabDetails.Fields.Tags.Label')}</IPLabel>
            </TextInfo>
            <KeyValueTagPicker
              selectedTags={[
                ...appContext.globalDataCache.tags.getItemsForId(props.processingActivity.tagIds),
                ...props.processingActivity.newTags,
              ]}
              onAdd={onAddTag}
              onRemove={onRemoveTag}
              onCreate={onCreateTag}
              allowCreate={hasUserFeatureGenericManager(appContext)}
            />
          </IPStack.Item>
        </IPStack>
      </IPStack>
    );
  };

  const getUnFocusChildren = (): JSX.Element | null => {
    if (!props.processingActivity) return null;

    return (
      <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { paddingBottom: 5 } }}>
        <IPStack horizontal wrap tokens={globalStackTokensPropertySet}>
          <PropertySetLabelValue
            label={t('processingActivity:TabDetails.Fields.Code.Label')}
            value={props.processingActivity.code}
          />
          <PropertySetLabelValue
            label={t('processingActivity:TabDetails.Fields.Owner.Label')}
            value={new Owner(props.processingActivity, appContext).name}
          />
          {hasUserFeature(appContext, FeatureTypes.RBAC) && (
            <PropertySetLabelValue
              label={t('adminAuth:AuthSchemaPicker.Label')}
              value={
                appContext.globalDataCache.authSchemas.items.find(
                  (u) => u.authSchemaId === props.processingActivity?.authSchemaId,
                )?.name ?? t('adminAuth:AuthSchemaPicker.Placeholder')
              }
            />
          )}
          <PropertySetLabelValue
            label={t('processingActivity:TabDetails.Fields.Role')}
            value={props.processingActivity.getRoleText(t as TFunction<string[]>)}
          />
          <PropertySetLabelValue
            label={t('processingActivity:TabDetails.Fields.State')}
            value={props.processingActivity.getStateText(t as TFunction<string[]>)}
          />
          {props.processingActivity.approvalDate &&
            props.processingActivity.state !== ProcessingActivityState.AwaitingApproval && (
              <PropertySetLabelValue
                label={t('processingActivity:TabDetails.Fields.ApprovalDate')}
                value={
                  <IPText variant="large">
                    <HrefLink onClick={openReport}>
                      {props.processingActivity.state === ProcessingActivityState.Concept
                        ? t('processingActivity:TabDetails.Fields.ApprovalDateRejected', {
                            date: toLocaleDateShort(props.processingActivity.approvalDate),
                          })
                        : toLocaleDateShort(props.processingActivity.approvalDate)}
                    </HrefLink>
                  </IPText>
                }
              />
            )}
          <PropertySetLabelValue
            label={t('processingActivity:TabDetails.Fields.StartDate')}
            value={toLocaleDateShort(props.processingActivity.startDate)}
          />
          <PropertySetLabelValue
            label={t('processingActivity:TabDetails.Fields.EndDate')}
            value={toLocaleDateShort(props.processingActivity.endDate)}
          />
          <PropertySetLabelValue
            label={t('processingActivity:TabDetails.Fields.RetentionPeriod')}
            value={
              props.processingActivity.retentionPeriod
                ? props.processingActivity.retentionPeriod.value
                : getRetentionPeriodText()
            }
          />
        </IPStack>
        {props.processingActivity?.tagIds && props.processingActivity.tagIds.length > 0 && (
          <IPStack.Item>
            <KeyValueTags tags={appContext.globalDataCache.tags.getItemsForId(props.processingActivity.tagIds)} />
          </IPStack.Item>
        )}
      </IPStack>
    );
  };

  //
  // Main render
  //
  if (!props.processingActivity) return null;

  return (
    <PropertySet
      unFocusKey={props.unFocusKey}
      focusChildren={getFocusChildren()}
      unFocusChildren={getUnFocusChildren()}
      readOnly={props.readOnly}
    />
  );
};
